import {
  getRequest,
  patchRequest,
  postRequest,
  deleteRequest,
  downloadRequest,
} from "../utils/Axios";

/**
 * Fetches all of the projects
 * @returns Promise<ProjectsApiResult>
 */
export async function fetchProjects(): Promise<GenericApiResult> {
  let loading = true;
  let error: string | null = null;
  let data: never[] = [];

  await getRequest("projects")
    .then((response) => {
      data = response.data;
      error = null;
      loading = false;
    })
    .catch((err) => {
      error = err.message;
      loading = false;
    });

  return {
    loading,
    error,
    data,
  };
}

/**
 * Fetches a single project by id
 * @param string|undefined id
 * @returns Promise<ProjectApiResult>
 */
export async function fetchProject(id: string | undefined): Promise<ProjectApiResult> {
  let loading = true;
  let error: string | null = null;
  let data: ProjectData | null = null;

  if (typeof id !== "string") {
    loading = false;
    error = "Project id missing from request";
    return {
      loading,
      error,
      data,
    };
  }

  await getRequest("projects/" + id)
    .then((response) => {
      data = response.data;
      error = null;
      loading = false;
    })
    .catch((err) => {
      error = err.message;
      loading = false;
    });

  return {
    loading,
    error,
    data,
  };
}

/**
 * Create a project
 * @param ProjectCreateVars input
 * @returns Promise<ProjectCreateData>
 */
export async function createProject(input: ProjectCreateVars): Promise<ProjectCreateData> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  await postRequest("projects", input)
    .then((response) => {
      const data = response.data.projectCreate;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectCreate;
      messages = data.messages;
    });

  return {
    projectCreate: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Update a project
 * @param ProjectUpdateVars input
 * @returns Promise<ProjectUpdate>
 */
export async function updateProject(
  id: string | undefined,
  input: ProjectUpdateVars
): Promise<ProjectUpdate> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  if (typeof id !== "string") {
    return {
      projectUpdate: {
        result: result,
        messages: [{ field: "base", message: "Id missing from request" }],
      },
    };
  }

  await patchRequest("projects/" + id, input)
    .then((response) => {
      const data = response.data.projectUpdate;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectUpdate;
      messages = data.messages;
    });

  return {
    projectUpdate: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Delete a single project by id
 * @param string|undefined id
 * @returns Promise<ProjectDeleteData>
 */
export async function deleteProject(id: string | undefined): Promise<ProjectDeleteData> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  if (typeof id !== "string") {
    messages.push("An error occurred while deleting the Project");
    return {
      projectDelete: {
        result: result,
        messages: messages,
      },
    };
  }

  await deleteRequest("projects/" + id)
    .then((response) => {
      const data = response.data.projectDelete;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectDelete;
      messages = data.messages;
    });

  return {
    projectDelete: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Prepare a survey respondent
 * @param ResponsePrepareVars input
 * @returns Promise<ResponsePrepareData>
 */
export async function prepareResponse(input: ResponsePrepareVars): Promise<ResponsePrepareData> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  await postRequest("project-response", input)
    .then((response) => {
      const data = response.data.responsePrepare;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.responsePrepare;
      messages = data.messages;
    });

  return {
    responsePrepare: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Finalize a survey respondent
 * @param ResponseFinalizeVars input
 * @returns Promise<ResponseFinalizeData>
 */
export async function finalizeResponse(input: ResponseFinalizeVars): Promise<ResponseFinalizeData> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  await patchRequest("project-response", input)
    .then((response) => {
      const data = response.data.responseFinalize;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.responseFinalize;
      messages = data.messages;
    });

  return {
    responseFinalize: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Batch update project responses
 * @param ProjectResponseStatusBatchUpdateVars input
 * @returns Promise<ProjectResponseStatusBatchUpdatedAt>
 */
export async function updateResponses(
  input: ProjectResponseStatusBatchUpdateVars
): Promise<ProjectResponseStatusBatchUpdatedAt> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  await patchRequest("project-responses", input)
    .then((response) => {
      const data = response.data.projectResponseStatusBatchUpdate;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectResponseStatusBatchUpdate;
      messages = data.messages;
    });

  return {
    projectResponseStatusBatchUpdate: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Submits pre survey answers
 * @param project_supplier_id 
 * @param answers 
 * @param respondent_id 
 * @param roundtable_session_id
 * @returns 
 */
export async function submitPreSurvey(
  project_supplier_id: string,
  answers: PreSurveyAnswer[],
  respondent_id: string,
  roundtable_session_id: string | null = null
): Promise<ProjectSupplierPreSurveySubmit> {
  let result: undefined | Project = undefined,
    messages: string[] = [];

  await postRequest(`project-suppliers/${project_supplier_id}/pre-survey`, {
    answers: answers,
    respondent_id: respondent_id,
    roundtable_session_id: roundtable_session_id,
  })
    .then((response) => {
      const data = response.data.ProjectSupplierPreSurveySubmit;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.ProjectSupplierPreSurveySubmit;
      messages = data.messages;
    });

  return {
    ProjectSupplierPreSurveySubmit: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Retrieves flagged respondent answers for a project supplier
 * @param project_supplier_id
 * @returns Promise<ProjectSupplierFlaggedAnswers>
 */
export async function getProjectSupplierFlaggedAnswers(
  project_supplier_id: string
): Promise<ProjectSupplierFlaggedAnswers> {
  let result: ProjectSupplierAnswer[] = [],
    messages: string[] = [];

  await getRequest(`project-suppliers/${project_supplier_id}/flagged-answers`)
    .then((response) => {
      const data = response.data.ProjectSupplierFlaggedAnswers;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.ProjectSupplierFlaggedAnswers;
      messages = data.messages;
    });

  return {
    ProjectSupplierFlaggedAnswers: {
      result: result,
      messages: messages,
    },
  };
}

/**
 * Sends the forensiq device data for a respondent
 * @param respondent_id
 * @param forensiq_data
 * @returns
 */
export async function updateRespondentDevice(
  respondent_id: string,
  forensiq_data: JsonObject
): Promise<RespondentDeviceForensiq> {
  let result = false,
    messages: string[] = [];

  await postRequest(`project-respondent/${respondent_id}/device-forensiq`, {
    data: JSON.stringify(forensiq_data),
  })
    .then((response) => {
      const data = response.data.RespondentDeviceForensiq;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.RespondentDeviceForensiq;
      messages = data.messages;
    });

  return {
    RespondentDeviceForensiq: {
      result: result,
      messages: messages,
    },
  };
}

export async function getRespondentDeviceForensiq(
  respondent_id: string
): Promise<ProjectResponseDeviceForensiq> {
  let result: RespondentDeviceForensiq | null = null,
    messages: string[] = [];

  await getRequest(`project-respondent/${respondent_id}/device-forensiq`)
    .then((response) => {
      const data = response.data.projectResponseDeviceForensiq;
      result = data.result || null;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectResponseDeviceForensiq;
      result = null;
      messages = data.messages;
    });

  return {
    projectResponseDeviceForensiq: {
      result: result,
      messages: messages,
    },
  };
}

export function downloadProjectResponses(
  projectId: string,
  projectSupplierId?: string,
  opts: FetchResponsesOptions = {}
) {
  const params = {
    status: opts.status || null,
    search: opts.search || null,
    sortField: opts.sortField || null,
    sortOrder: opts.sortOrder || null,
  };
  const postParams = {
    data: JSON.stringify(params),
  };
  if (projectSupplierId) {
    return downloadRequest(`projects/${projectId}/${projectSupplierId}/download`, postParams);
  } else {
    return downloadRequest(`projects/${projectId}/download`, postParams);
  }
}

export function downloadFlaggedAnswers(
  projectSupplierId: string,
  opts: FetchResponsesOptions = {}
) {
  const params = {
    search: opts.search || null,
    sortField: opts.sortField || null,
    sortOrder: opts.sortOrder || null,
  };
  const postParams = {
    data: JSON.stringify(params),
  };
  return downloadRequest(`project-supplier/${projectSupplierId}/download-flagged-answers`, postParams);
}

export type FetchResponsesOptions = {
  page?: number;
  limit?: number;
  status?: string;
  projectSupplierId?: string;
  search?: string;
  sortField?: string;
  sortOrder?: string;
};

type FetchPaginatedResponse = {
  data: ProjectResponse[];
  meta: { page: number; limit: number; total: number; totalPages: number };
};

export async function fetchProjectResponses(projectId: string, opts: FetchResponsesOptions = {}) {
  const params = {
    page: opts.page ?? 1,
    limit: opts.limit ?? 50,
    status: opts.status || null,
    projectSupplierId: opts.projectSupplierId || null,
    search: opts.search || null,
    sortField: opts.sortField || null,
    sortOrder: opts.sortOrder || null,
  };
  let loading = true;
  let error: string | null = null;
  let data: FetchPaginatedResponse | null;

  await postRequest(`project-responses-list/${projectId}`, {
    data: JSON.stringify(params),
  })
    .then((response) => {
      data = response.data;
      error = null;
      loading = false;
    })
    .catch((err) => {
      error = err.message;
      loading = false;
    });
  return { error, data: data.data, meta: data?.meta, loading };
}

export async function getAllPms() {
  let result: string[] | null = null,
    messages: string[] = [];

  await getRequest(`project-managers/all`)
    .then((response) => {
      const data = response.data.ProjectManagers;
      result = data.result || null;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.ProjectManagers;
      result = null;
      messages = data.messages;
    });

  return {
    result: result,
    messages: messages,
  };
}

/**
 * Update a project's supplier
 * @param string projectId
 * @param string projectSupplierId
 * @param ProjectSupplierVars input
 * @returns
 */
export async function updateProjectSupplier(
  projectId: string | undefined,
  projectSupplierId: string | undefined,
  input: ProjectSupplierVars
) {
  let result: undefined | ProjectSupplier = undefined,
    messages: string[] = [];

  if (typeof projectId !== "string" || typeof projectSupplierId !== "string") {
    return {
      projectSupplierUpdate: {
        result: result,
        messages: [{ field: "base", message: "Ids missing from request" }],
      },
    };
  }

  await patchRequest("project-supplier/" + projectId + "/" + projectSupplierId, input)
    .then((response) => {
      const data = response.data.projectSupplierUpdate;
      result = data.result;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectSupplierUpdate;
      messages = data.messages;
    });

  return {
    projectSupplierUpdate: {
      result: result,
      messages: messages,
    },
  };
}

export async function getRespondentProofOfHuman(
  respondent_id: string
): Promise<ProjectResponseProofOfHuman> {
  let result: RespondentProofOfHuman | null = null,
    messages: string[] = [];

  await getRequest(`project-respondent/${respondent_id}/proof-of-human`)
    .then((response) => {
      const data = response.data.projectResponseProofOfHuman;
      result = data.result || null;
      messages = data.messages;
    })
    .catch((err) => {
      const data = err.response.data.projectResponseProofOfHuman;
      result = null;
      messages = data.messages;
    });

  return {
    projectResponseProofOfHuman: {
      result: result,
      messages: messages,
    },
  };
}
